import { useState, useEffect } from 'react';
import type { ManifestStatus } from '@/types/manifest';
import { useManifests } from '../hooks/use-manifests';
import { useDebounce } from '@/hooks/useDebounce';
import { ManifestList } from '../components/manifest-list';
import { CreateManifestModal } from '../components/create-manifest-modal';

type FilterTab = 'all' | ManifestStatus;

export function ManifestsPage() {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const debouncedName = useDebounce(nameInput, 400);
  const statusFilter = activeTab === 'all' ? undefined : activeTab;
  const { manifests, isLoading, error, refetch, backgroundRefetch } = useManifests(
    statusFilter,
    debouncedName || undefined,
    dateFrom || undefined,
    dateTo || undefined,
  );

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      backgroundRefetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, backgroundRefetch]);

  const tabs: { value: FilterTab; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'Open', label: 'Open' },
    { value: 'InProgress', label: 'In Progress' },
    { value: 'Complete', label: 'Complete' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Manifests</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          >
            Create Manifest
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {tabs.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setActiveTab(value)}
            className={`px-4 py-2 text-sm rounded-t transition-colors ${
              activeTab === value
                ? 'bg-gray-800 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="Search by barge name..."
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500 text-sm"
          />
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400 whitespace-nowrap">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400 whitespace-nowrap">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>
          {(nameInput || dateFrom || dateTo) && (
            <button
              onClick={() => { setNameInput(''); setDateFrom(''); setDateTo(''); }}
              className="px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm transition-colors whitespace-nowrap"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <ManifestList manifests={manifests} isLoading={isLoading} error={error} />

      <CreateManifestModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={refetch}
      />
    </div>
  );
}
